const MAX_ANCESTOR_DEPTH = 4;
const SKIPPED_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT", "OPTION", "BUTTON", "svg", "SVG"]);
// Elements that already explain themselves: a native title, a PrimeReact
// data-attribute tooltip, an open tooltip, or an explicit opt-out.
const EXISTING_TOOLTIP_SELECTOR =
  "[title], [data-pr-tooltip], .p-tooltip, .truncation-tooltip, [data-truncation-tooltip='off']";
// Opt-in container (e.g. a table cell with a value and its share line) whose
// full text is shown when any of its lines is truncated.
const TRUNCATION_SCOPE_SELECTOR = "[data-truncation-scope]";

const hasLineClamp = (style) => Boolean(style?.webkitLineClamp) && style.webkitLineClamp !== "none";

// True when an element's text is cut by CSS: a multi-line clamp overflows
// vertically, a single-line ellipsis overflows horizontally. Checking only the
// relevant axis avoids false positives from line-height rounding.
export const isTextTruncated = (element, style) => {
  if (!element) return false;
  if (hasLineClamp(style)) return element.scrollHeight > element.clientHeight + 1;
  return element.scrollWidth > element.clientWidth;
};

// `text-overflow: ellipsis` has no visual effect while overflow remains
// visible. This matters for the first table header: it contains selection
// controls and deliberately keeps its overflow visible, so it must not be
// treated as truncated merely because it inherits the ellipsis declaration.
const truncatesText = (style) =>
  (style?.textOverflow === "ellipsis" && (style?.overflowX || style?.overflow) !== "visible") ||
  hasLineClamp(style);

const normalizeText = (value) => String(value || "").replace(/\s+/g, " ").trim();

// Keeps each block line (a value and the share line below it) on its own line
// instead of gluing them together. Use source text for transformed text so CSS
// such as `text-transform: uppercase` does not leak into the tooltip.
const readText = (element, style) => {
  const hasTextTransform = style?.textTransform && style.textTransform !== "none";
  const raw = !hasTextTransform && typeof element.innerText === "string"
    ? element.innerText
    : element.textContent;
  return String(raw || "").split("\n").map(normalizeText).filter(Boolean).join("\n");
};

/**
 * Finds the element, starting at the hovered node and walking up a few
 * ancestors, whose text is truncated by CSS. Returns the element and its full
 * text, or null when nothing is truncated or the element already has a tooltip.
 */
export const findTruncatedText = (startNode, getStyle = (element) => window.getComputedStyle(element)) => {
  let element = startNode?.nodeType === 1 ? startNode : startNode?.parentElement;

  for (let depth = 0; element && depth < MAX_ANCESTOR_DEPTH; depth += 1) {
    if (SKIPPED_TAGS.has(element.tagName)) return null;
    if (element.closest?.(EXISTING_TOOLTIP_SELECTOR)) return null;

    const style = getStyle(element);
    if (truncatesText(style) && isTextTruncated(element, style)) {
      const target = element.closest?.(TRUNCATION_SCOPE_SELECTOR) || element;
      const text = readText(target, getStyle(target));
      return text ? { element: target, text } : null;
    }

    element = element.parentElement;
  }

  return null;
};
